<template>
    <card>
        <card-header>Import CSV Contacts</card-header>
        <card-body>
            <div class="form-group">
                <label for="exampleFormControlSelect1">Please Select File Upload Type</label>
                <select @change="selectFileType" class="form-control" id="exampleFormControlSelect1" >
                    <option v-for="(type, key) in fileTypeUrl" :value="`${key}`">{{type.name}}</option>
                </select>
            </div>
            <div class="w-75 m-auto text-center">
                <div class="animated pulse">
                    <h4 class="transitioned-slow mb-2" :class="{'mt-5': !uploaded}">Upload Contacts From CSV File</h4>
                    <drag-drop-file-uploader class="transitioned-slow" ref="uploader" :url="url"
                                             accept=".csv" button-class="btn btn-block btn-primary swell-btn"
                                             :upload-on-select="true" @uploaded="onUpload"></drag-drop-file-uploader>
                    <div class="mb-2" :class="{'mb-5': !uploaded}" v-if="fileType == 'default'">
                        <small>The CSV columns must follow this order: <b>name</b>, <b>email</b> and <b>phone number</b>.<br>
                        </small>
                        <small><a class="cursor-pointer" :href="sample_endpoint">Download sample CSV file</a></small>
                    </div>
                </div>
            </div>
            <div class="animated animated-normal fadeIn" v-if="uploaded">
                <div class="form-group" v-if="fileType == 'default'">
                    <label for="inputApplyAllLocations"> Select a Location: </label>
                    <multiselect :options="locations" v-model="locationAll" track-by="id"
                                 id="inputApplyAllLocations" name="inputApplyAllLocations"
                                 label="name" @input="replicateLocations"></multiselect>
                </div>
                <div class="form-group" v-else>
                    <div v-if="fileLocations.length">
                        <h4>Map File Locations</h4>
                        <hr>
                        <div class="row mt-1 border-bottom" :class="{'border-success': fileLocation.swell_location}" v-for="fileLocation in fileLocations">
                            <div v-if="!fileLocation.campaigns_available">
                                <div class="alert alert-warning" role="alert" >
                                    Location selected does not have an campaign/template available, you will not be able to send invites after importing contacts without a campaign.
                                </div>
                            </div>
                            <div class="col-md-6">
                                <b> {{fileLocation.name}} </b>
                            </div>
                            <br>
                            <div class="col-md-6">
                                <multiselect :options="locations" :id="`fileLocationInput-${fileLocation.index}`" v-model="fileLocation.swell_location"
                                             :name="`swell_locations-${fileLocation.index}`" label="name" @input="pairFileLocation($event, fileLocation.index)"></multiselect>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div v-if="!validFile">
                            <span class="text-danger"> File provided did not have sufficient data for processing, ensure file contains valid file headers.</span>
                        </div>
                        <div v-else>
                            <span class="text-danger"> No Locations Found In File </span>
                        </div>
                    </div>
                </div>
                <div v-if="messages">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label class="mb-0">
                                    <input type="checkbox" @click="updateImportMessages" :checked="importMessages"/>
                                    Import Messages
                                </label>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div v-if="importMessages" class="form-group">
                                <label class="mb-0">
                                    <input type="checkbox" @click="updateMarkMessagesAsUnread" :checked="messagesMarkAsUnread"/>
                                    Mark Imported Messages As <b><u>Unread</u></b>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!locationsSet && !showContacts">
                    <div class="alert alert-info" role="alert" v-if="fileType == 'default'">
                        Please select a location to continue.
                    </div>
                    <div class="alert alert-info" role="alert" v-if="fileType != 'default' && fileLocations.length">
                        Locations not set, please pair each location from file to an existing Swell location to continue.
                    </div>
                </div>
                <div v-else>
                    <div class="form-group">
                        <label for="inputApplyAllContacts"> Select a Group </label><small data-html="true" v-tooltip="tooltip()"> (*optional) </small>:
                        <multiselect :options="groups" v-model="groupAll" track-by="id" id="inputApplyAllContacts"
                                     name="inputApplyAllContacts" label="name" @input="replicateLocations"></multiselect>
                    </div>
                    <hr/>
                    <h5 class="text-center">Contacts Preview</h5>
                    <div class="alert alert-danger animated pulse" v-if="form.errors.any()">
                        <button class="close" type="button" @click="form.errors.clear()"><span
                            aria-hidden="true">&times;</span></button>
                        <b>Errors</b>
                        <ul>
                            <li v-for="(message, index) of form.errors.errors" :key="`message-errors-${index}`">{{ message[0] }}</li>
                        </ul>
                    </div>

                    <div class="alert alert-info" role="alert">
                        Contacts listed below with a "Line Error" will not be processed on upload. Resolve line errors in file and re-upload as needed.
                    </div>
                    <v-client-table ref="table" :data="imported_contacts"
                                    :columns="['file_line_number', 'name', 'email', 'phone_number', 'location', 'group', 'line_errors', 'actions']"
                                    :options="options">


                        <div slot="location" slot-scope="props">
                            <span v-if="imported_contacts[props.index-1].location && imported_contacts[props.index-1].location.name">
                              {{imported_contacts[props.index-1].location.name}}
                            </span>
                            <span v-else>
                              Location field empty on file
                            </span>
                        </div>

                        <div slot="group" slot-scope="props">
                            <span v-if="imported_contacts[props.index-1].group && imported_contacts[props.index-1].group.name">
                              {{imported_contacts[props.index-1].group.name}}
                            </span>
                            <span v-else>
                              N/A
                            </span>
                        </div>

                        <div slot="line_errors" slot-scope="props">
                            <transition name="fade-in-down">
                                <div v-if="imported_contacts[props.index-1].line_errors.length">
                                <span class="badge px-2 py-1 text-uppercase has-more badge-danger cursor-pointer"
                                      data-toggle="collapse"
                                      :data-target="`#accordion${props.index-1}`">
                                    ERRORS!
                                    <i class="fa fa-chevron-down"></i>
                                </span>
                                    <ul :id="`accordion${props.index-1}`" class="collapse p-1">
                                        <li class="text-danger" v-for="import_error in imported_contacts[props.index-1].line_errors">
                                            <small> {{import_error}} </small>
                                        </li>
                                    </ul>
                                </div>
                            </transition>
                            <transition name="fade-in-down">
                                <div v-if="imported_contacts[props.index-1].warnings.length">
                                <span class="badge px-2 py-1 text-uppercase has-more badge-warning cursor-pointer"
                                      data-toggle="collapse"
                                      :data-target="`#accordion${props.index-1}`">
                                    Warning
                                    <i class="fa fa-chevron-down"></i>
                                </span>
                                    <ul :id="`accordion${props.index-1}`" class="collapse p-1">
                                        <li class="text-danger" v-for="warning in imported_contacts[props.index-1].warnings">
                                            <small> {{warning}} </small>
                                        </li>
                                    </ul>
                                </div>
                            </transition>
                        </div>

                        <button class="btn btn-xs btn-danger" slot="actions" slot-scope="props"
                                @click="remove(props.row, props.index-1)"><i class="fa fa-trash"></i></button>
                    </v-client-table>
                </div>
                <div class="text-center mt-4">
                    <button-loading class="btn btn-primary ml-2" @click="importContacts()" v-if="uploaded && locationsSet"
                                    :loading="form.submitting">
                        <i class="fa fa-download mr-2" v-if="!form.submitting"></i>Import Contacts
                    </button-loading>
                </div>
            </div>
            <modal ref="importedContactsModal" :has-footer="true" size="lg"><span slot="title">Send Review Invites to Imported Contacts
            <div v-if="locationFocus">
                ON LOCATION <b>{{locationFocus.name}} </b>
            </div>
            </span>
                <p class="small">{{ quantityImported }} contacts have
                    been&nbsp;<b>imported</b>&nbsp;and&nbsp;<b>saved.</b>Now you can select which contacts will receive
                    an invite.</p>

                <v-client-table id="send-invite-table" ref="sendInviteTable" :data="form.contacts"
                                :columns="['select', 'name', 'email', 'phone_number']" :options="options">
                    <template slot="beforeTable">
                        <div class="d-flex flex-column my-3">
                            <div class="mb-1">
                                <input class="mr-2 p-0" type="checkbox" id="contacts-all" :checked="allContactsSelected"
                                       @click="handleAllContacts"/>
                                <label class="m-0 p-0" for="contacts-all">All Contacts</label>
                            </div>
                            <div><span class="ml-4" v-if="markedRows.length">{{ markedRowsQuantity }}</span></div>
                        </div>
                    </template>

                    <template slot="select" slot-scope="props">
                        <input type="checkbox" :value="props.row.id" v-model="markedRows"/>
                    </template>
                </v-client-table>

                <div slot="footer">
                    <button-loading class="btn btn-secondary ml-1" type="button" @click="closeImportedContactsModal">Close
                    </button-loading>
                    <button-loading class="btn btn-primary ml-1" type="button" :loading="form.submitting"
                                    @click="sendInvite" v-if="markedRows.length">Continue
                    </button-loading>
                </div>
            </modal>
            <send-bulk-invites-modal ref="openBulkInvitesModal" :contacts="markedRows" :preselectedLocation="locationFocus" @inviteProcessComplete="handleNextLocation"></send-bulk-invites-modal>
        </card-body>
    </card>
</template>

<script>
    export default {
        data: () => ({
            imported_contacts: [],
            uploaded         : false,
            options          : {
                sortable: [
                    'file_line_number',
                    'name',
                    'email',
                    'phone_number',
                ]
            },
            form             : new Form({
                contacts: [],
                messages: {
                    importMessages: false,
                    messagesMarkAsUnread: false
                },
            }),
            sample_endpoint  : laroute.route('contacts.import.download-csv-sample'),
            markedRows       : [],
            quantityImported : null,
            locations        : [],
            groups           : [],
            locationAll      : null,
            groupAll         : null,
            contactsImported : [],
            fileType         : 'default',
            url              : laroute.route('api.files.read-csv', {type: 'default'}),
            fileTypeUrl      : {
                'default': {
                    'name': 'Swell Contact CSV (default)',
                    'param' : 'default'
                },
                'client1': {
                    'name': 'PCC CSV Upload',
                    'param' : 'client1'
                },
                'client2': {
                    'name': 'OP CSV Upload',
                    'param' : 'client2'
                },
                'client3': {
                    'name': 'MicroMD CSV Upload',
                    'param' : 'client3'
                },
                'client4': {
                    'name': 'Podium Chat CSV Upload',
                    'param' : 'client4'
                },
                'client5': {
                    'name': 'MD Suite CSV Upload',
                    'param' : 'client5'
                }
            },
            fileLocations    : [],
            showContacts     : false,
            selectedLocations: {},
            finishedImporting: false,
            locationFocus    : null,
            validFile        : true,
            messagesMarkAsUnread: false,
            importMessages: false,
            messages: false,
        }),

        created() {
            this.fetchLocations();
            this.fetchGroups();
        },

        computed: {
            locationsSet() {
                if(this.fileType == 'default'){
                    if(!this.locationAll){
                        return false;
                    }
                }else{
                    if(!this.fileLocations || !this.fileLocations.length){
                        return false;
                    }
                    var allLocationsSet = true;
                    this.fileLocations.forEach(function(element) {
                        if(!element.swell_location){
                            allLocationsSet = false;
                        }
                    });
                    return allLocationsSet;
                }
                return true;
            },

            contacts() {
                return this.imported_contacts;
            },

            allContactsSelected() {
                if (this.form.contacts) {
                    return _.difference(this.form.contacts.map(c => c.id), this.markedRows).length === 0;
                }
            },

            markedRowsQuantity() {
                return this.markedRows.length > 1 ? `${this.markedRows.length} contacts selected` : `${this.markedRows.length} contact selected`;
            },

            pages() {
                return Math.ceil(this.imported_contacts.length / 100);
            }
        },

        methods: {
            tooltip() {
                return {title:'To create a group visit the <u>Contact Group</u> tab above.'};
            },
            handleNextLocation(){
                let lastKey = 0;
                if(Object.keys(this.selectedLocations).length > 0){
                    for (let key in this.selectedLocations) {
                        if (this.selectedLocations.hasOwnProperty(key)) {
                            lastKey = key;
                        }
                    }
                }
                if(lastKey > 0){
                    this.afterImport(lastKey)
                }
            },
            async pairFileLocation(e, index) {
                if(this.fileLocations[index].swell_location){
                    let swell_location_id = this.fileLocations[index].last_swell_location;
                    if(this.selectedLocations.hasOwnProperty(swell_location_id)) {
                        if(this.selectedLocations[swell_location_id].includes(index)) {
                            if(this.selectedLocations[swell_location_id].length == 1){
                                delete this.selectedLocations[swell_location_id];
                            }else{
                                for(var i = 0; i < this.selectedLocations[swell_location_id].length; i++){
                                    if(this.selectedLocations[swell_location_id][i] === index){
                                        this.selectedLocations[swell_location_id].splice(i,1);
                                    }
                                }
                            }
                        }
                    }
                    this.fileLocations[index].last_swell_location = null;
                }
                this.fileLocations[index].swell_location = e;
                if(!this.fileLocations[index].last_swell_location){
                    this.fileLocations[index].last_swell_location = e.id
                }
                this.locationCampaignsAvailable(e, index);

                var allLocationsSet = true;
                this.fileLocations.forEach(function(element) {
                    if(!element.swell_location){
                        allLocationsSet = false;
                    }
                });
                if(allLocationsSet){
                    await this.validateFileContent(this.imported_contacts);
                    this.showContacts = true;
                }else{
                    this.showContacts = false;
                }
            },

            selectFileType(e) {
                this.fileType = e.target.value;
                this.url = laroute.route('api.files.read-csv', {type: this.fileTypeUrl[this.fileType].param});
                this.resetFormAndUploadFields();
            },
            openModal() {
                this.$refs.importedContactsModal.open();
            },
            closeModal() {
                this.$refs.importedContactsModal.close();
            },

            async onUpload(imported_contacts) {
                this.softReset();
                if(this.fileType == 'default'){
                    this.imported_contacts = imported_contacts.data.map((arr, index) => ({
                        index,
                        file_line_number: index + 1,
                        name            : arr[0],
                        email           : arr[1],
                        phone_number    : arr[2],
                        location        : this.locationAll,
                        group           : this.groupAll,
                        line_errors     : 'processing',
                        warnings        : arr[4]
                    }));
                    await this.validateFileContent(this.imported_contacts);
                }else{
                    this.validFile = imported_contacts.data.validFile;
                    this.fileLocations = imported_contacts.data.locations.map((value, index) => ({
                        index,
                        name: value,
                        swell_location: null,
                        last_swell_location:null,
                        campaigns_available: true,
                    }));
                    this.imported_contacts = imported_contacts.data.data.map((arr, index) => ({
                        index,
                        file_line_number: index + 1,
                        name            : arr[0],
                        email           : arr[1],
                        phone_number    : arr[2],
                        location        : arr[3],
                        file_location   : arr[3],
                        group           : this.groupAll,
                        line_errors     : 'processing',
                        warnings        : arr[4],
                        message         : (typeof arr[5] === 'undefined') ? null : arr[5],
                    }));
                }
                this.uploaded          = true;
            },

            async validateFileContent(imported_contacts){
                await this.$http.post(this.$laroute.route('api.contacts.cleanCsvFileData',
                    {fileType: this.fileType}),
                    {contacts: imported_contacts, fileLocations: this.fileLocations})
                    .then(({data}) => {
                        this.imported_contacts = data;
                    });
                return;
            },
            softReset() {
                this.messages = (this.fileType === 'client4');
                this.importMessages = this.messages;
                this.messagesMarkAsUnread = false;
                this.selectedLocations = {};
                this.finishedImporting = false;
                this.locationFocus = null;
                this.groupAll = false;
                this.showContacts = false;
                this.fileLocations = [];
                this.imported_contacts = [];
                this.contactsImported = [];
                this.locationAll = false;
                this.form =  new Form({
                    contacts: [],
                    messages: {
                        importMessages: this.importMessages,
                        messagesMarkAsUnread: this.messagesMarkAsUnread
                    },
                });
            },

            reset() {
                this.messages = false;
                this.importMessages = this.messages;
                this.messagesMarkAsUnread = false;
                this.uploaded          = false;
                this.imported_contacts = [];
                this.form = new Form({
                    contacts: [],
                    messages: {
                        importMessages: false,
                        messagesMarkAsUnread: false
                    },
                });
                this.$refs.uploader.clearAll();
            },

            resetFormAndUploadFields() {
                this.messages = (this.fileType === 'client4');
                this.importMessages = this.messages;
                this.messagesMarkAsUnread = false;
                this.uploaded = false;
                this.locationAll = null;
                this.groupAll = null;
                this.fileLocations = [];
                this.showContacts = false;
                this.form = new Form({
                    contacts: [],
                    messages: {
                        importMessages: this.importMessages,
                        messagesMarkAsUnread: this.messagesMarkAsUnread
                    },
                });
                this.$refs.uploader.clearAll();
            },

            remove(contact, index) {
                this.imported_contacts.splice(_.findIndex(this.imported_contacts, contact), 1);
            },

            filterContactsIntoLocations(){
                let total_selected_locations = Object.keys(this.selectedLocations).length;
                if(total_selected_locations >= 0){
                    for (let key in this.selectedLocations) {
                        if (this.selectedLocations.hasOwnProperty(key)) {
                            let firstContact = true;
                            this.selectedLocations[key] = {'contacts':[],'imported_contacts':[], 'location':null}
                            for (var i = 0; i < this.imported_contacts.length; i++) {
                                if(!this.imported_contacts[i].location){
                                }else{
                                    if(this.imported_contacts[i].location.id == key){
                                        if(firstContact){
                                            this.selectedLocations[key]['location'] = this.imported_contacts[i].location;
                                        }
                                        this.selectedLocations[key]['contacts'].push(this.imported_contacts[i]);
                                    }
                                }
                            }
                        }
                    }
                }
            },

            afterImport(selected_location_id = 0) {
                if(Object.keys(this.selectedLocations).length > 0){
                    this.locationFocus = this.selectedLocations[selected_location_id]['location'];
                    let importedContactArray = this.selectedLocations[selected_location_id]['imported_contacts'];
                    this.contactsImported = importedContactArray;
                }
                this.quantityImported = _.uniqBy(this.contactsImported, s => s.id).length;
                this.resetFormAndUploadFields();

                this.markedRows    = [];
                this.imported_contacts = _.uniqBy(this.contactsImported, s => s.id);
                this.form.contacts = this.imported_contacts;
                this.handleAllContacts();
                this.openModal();
                if(selected_location_id > 0){
                    delete this.selectedLocations[selected_location_id];
                }
            },

            async importContacts(begin, end = 100, count = 1, selected_location_id = 0) {
                let start = begin || 0;
                let total_selected_locations = Object.keys(this.selectedLocations).length;
                if(start == 0 && total_selected_locations > 0 && selected_location_id == 0){
                    this.filterContactsIntoLocations();
                    for (let key in this.selectedLocations) {
                        if (this.selectedLocations.hasOwnProperty(key)) {
                            this.importContacts(start, end, 0, key);
                        }
                    }
                }else{
                    if(selected_location_id == 0 && total_selected_locations <= 0){
                        this.form.contacts = this.imported_contacts.slice(start, end);
                    }else{
                        if(selected_location_id <= 0){
                            return;
                        }
                        this.form.contacts = this.selectedLocations[selected_location_id]['contacts'].slice(start, end);
                    }
                    this.form.contacts = this.form.contacts.filter(contact => contact.line_errors.length <= 0);

                    this.form.fileType = this.fileType;

                    this.form.contacts = this.form.contacts.map((contact) => {
                        contact.location = { id: contact.location.id };

                        return contact;
                    });

                    await this.form.post(this.$laroute.route('api.contacts.import'))
                        .then(({data}) => {
                            count++;
                            if(selected_location_id > 0){
                                data.map(contact => this.selectedLocations[selected_location_id]['imported_contacts'].push(contact));
                            }else{
                                data.map(contact => this.contactsImported.push(contact));
                            }
                            if(selected_location_id > 0 && count < (this.selectedLocations[selected_location_id]['contacts'].length / 100)){
                                this.importContacts(start + 100, end + 100, count, selected_location_id);
                                return;
                            }

                            if (count <= this.pages && selected_location_id == 0) {
                                this.importContacts(start + 100, end + 100, count);
                                return;
                            }
                            if(!this.finishedImporting){
                                this.afterImport(selected_location_id);
                                this.finishedImporting = true;
                            }
                        });
                }
            },

            fetchLocations() {
                return this.$http.get(this.$laroute.route('api.locations.index'))
                    .then(({data}) => {
                        this.locations = data.data;
                    });
            },

            fetchGroups() {
                return this.$http.get(this.$laroute.route('groups.list'))
                    .then(({data}) => {
                        this.groups = data;
                    });
            },

            fetchCampaigns(location_id) {
                return this.$http.get(this.$laroute.route('api.locations.campaigns', {location: location_id}))
                    .then(({data}) => {
                        return !(!data.data.length);
                    }).catch(error => console.error(error));
            },

            async locationCampaignsAvailable(location, file_location_index){
                let available_campaigns = await this.fetchCampaigns(location.id);
                if(!available_campaigns){
                    this.fileLocations[file_location_index]['campaigns_available'] = false;
                    return;
                }
                this.fileLocations[file_location_index]['campaigns_available'] = true;
                if(!(this.selectedLocations.hasOwnProperty(location.id))){
                    this.selectedLocations[location.id] = [file_location_index];
                }else{
                    if(!this.selectedLocations[location.id].includes(file_location_index)){
                        this.selectedLocations[location.id].push(file_location_index);
                    }
                }
                return;
            },

            onImportSuccess(res) {
                const count_imported = res.data.length;
                this.$alert({
                    type   : 'success',
                    message: `${count_imported} contacts imported.`,
                }).then((res) => {
                    this.reset();
                });
            },

            handleAllContacts() {
                return this.allContactsSelected
                    ? this.markedRows = this.markedRows.filter(
                        sc => !this.form.contacts.map(c => c.id).includes(sc)
                    )
                    : this.form.contacts.map(
                        contact => !this.markedRows.includes(contact.id) ? this.markedRows.push(contact.id) : null
                    );
            },

            sendInvite() {
                this.$refs.importedContactsModal.close();
                this.$refs.openBulkInvitesModal.open();
            },

            closeImportedContactsModal() {
                this.$refs.importedContactsModal.close();
                this.handleNextLocation();
            },

            replicateLocations(type) {
                for (var i = 0; i < this.imported_contacts.length; i++) {
                    this.imported_contacts[i].location = this.locationAll;
                    this.imported_contacts[i].group    = this.groupAll;
                }
                if(this.fileType != 'default'){
                    this.validateFileContent(this.imported_contacts);
                }
            },

            updateImportMessages() {
                this.importMessages = !this.importMessages;
                this.form.messages.importMessages = this.importMessages;
            },

            updateMarkMessagesAsUnread() {
                this.messagesMarkAsUnread = !this.messagesMarkAsUnread;
                this.form.messages.messagesMarkAsUnread = this.messagesMarkAsUnread;
            }
        }
    }
</script>

<style lang="scss">
    #send-invite-table > .row > .col-md-12 {
        display: flex;
        justify-content: space-between;

        & .form-inline {
            margin-bottom: 0;
        }

        & .VueTables__limit > .VueTables__limit-field {
            display: flex;
            flex-wrap: wrap;

            & label {
                margin-right: 1em;
            }
        }
    }

    .replaceLocations {
        display: flex;
        flex: 1 50%;
        align-items: center;

        label {
            margin-right: .5rem;
            white-space: nowrap;
        }
    }

    .VueTables__search {


        &.pull-left {
            float: none;
        }

        &.form-inline {
            justify-content: space-between;
        }

        .VueTables__search-field {
            flex: 1 25%;
        }
    }
</style>
